import {SECOND} from "@shared/lib/timeUnits";

export type LeaderLockStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

type LeaderLock = {
    tabId: string
    expiresAt: number
}

export const LEADER_HEARTBEAT_INTERVAL = 10 * SECOND

// Chrome runs the timers of a tab hidden for 5+ minutes only once a minute, so the lease must outlive a few of
// those minutes, or a hidden leader would lose the lock to another tab every minute.
export const LEADER_LEASE_DURATION = 150 * SECOND

const LEADER_LOCK_KEY_PREFIX = 'orders-poller-leader:'

/**
 * Elects one tab of the browser for one poller key with a lease in localStorage:
 * the leader renews the lease every heartbeat, other tabs take the lock when it is free or expired.
 *
 * localStorage has no compare-and-set: two tabs starting at the same moment may both lead until the next heartbeat,
 * when the tab that did not write last sees the other tab's fresh lease and steps down. Two polls for a few seconds
 * are harmless. Without a usable localStorage every tab leads (polls), as before the election.
 */
export class TabLeaderElection {
    private isLeaderNow = false
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null
    private readonly listeners = new Set<() => void>()
    private readonly lockKey: string

    constructor(
        pollerKey: string,
        private readonly tabId: string,
        private readonly storage: LeaderLockStorage | null,
        private readonly getNow: () => number = Date.now,
    ) {
        this.lockKey = LEADER_LOCK_KEY_PREFIX + pollerKey
    }

    start(): void {
        if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer)

        this.tryToBecomeLeader()
        this.heartbeatTimer = setInterval(() => this.tryToBecomeLeader(), LEADER_HEARTBEAT_INTERVAL)
    }

    /**
     * @returns whether this tab was the leader, so the caller can tell the other tabs to take over at once.
     */
    stop(): boolean {
        if (this.heartbeatTimer !== null) {
            clearInterval(this.heartbeatTimer)
            this.heartbeatTimer = null
        }

        const wasLeader = this.isLeaderNow
        this.releaseLock()
        this.setIsLeader(false)

        return wasLeader
    }

    isLeader(): boolean {
        return this.isLeaderNow
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)

        return () => {
            this.listeners.delete(listener)
        }
    }

    /**
     * Runs every heartbeat, and at once when another tab says it released the lock.
     */
    tryToBecomeLeader(): void {
        if (this.storage === null) {
            this.setIsLeader(true)
            return
        }

        try {
            const lock = this.readLock()
            if (this.isLockedByAnotherTab(lock)) {
                this.setIsLeader(false)
                return
            }

            this.writeLock()
            this.setIsLeader(true)
        } catch (e) {
            // Storage is full or blocked: poll in this tab, as before the election.
            this.setIsLeader(true)
        }
    }

    private isLockedByAnotherTab(lock: LeaderLock | null): boolean {
        if (lock === null) return false
        if (lock.tabId === this.tabId) return false

        return lock.expiresAt > this.getNow()
    }

    private readLock(): LeaderLock | null {
        const storedLock = this.storage?.getItem(this.lockKey)
        if (!storedLock) return null

        try {
            const lock = JSON.parse(storedLock)
            if (typeof lock?.tabId !== 'string') return null
            if (typeof lock?.expiresAt !== 'number') return null

            return lock
        } catch (e) {
            return null
        }
    }

    private writeLock(): void {
        const lock: LeaderLock = {
            tabId: this.tabId,
            expiresAt: this.getNow() + LEADER_LEASE_DURATION,
        }
        this.storage?.setItem(this.lockKey, JSON.stringify(lock))
    }

    private releaseLock(): void {
        if (this.storage === null) return

        try {
            const lock = this.readLock()
            if (lock?.tabId !== this.tabId) return

            this.storage.removeItem(this.lockKey)
        } catch (e) {
            // Nothing to release: the lease expires on its own.
        }
    }

    private setIsLeader(isLeader: boolean): void {
        if (this.isLeaderNow === isLeader) return

        this.isLeaderNow = isLeader
        this.listeners.forEach(listener => listener())
    }
}
